import { useParams, Link } from 'react-router-dom';
import { Shield, Check, MapPin, Phone, ArrowRight } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import AccessibilityWidget from './AccessibilityWidget';

const LOCATIONS = {
  'brevard-county': { name: 'Brevard County', cities: 'Melbourne, Palm Bay, Titusville, Cocoa, Merritt Island, Rockledge, Viera' },
  'orange-county': { name: 'Orange County', cities: 'Orlando, Winter Park, Apopka, Ocoee, Winter Garden, Windermere, Dr. Phillips' },
  'osceola-county': { name: 'Osceola County', cities: 'Kissimmee, St. Cloud, Celebration, Poinciana, Harmony' },
  'seminole-county': { name: 'Seminole County', cities: 'Sanford, Lake Mary, Altamonte Springs, Longwood, Oviedo, Winter Springs' },
  'polk-county': { name: 'Polk County', cities: 'Lakeland, Winter Haven, Bartow, Haines City, Davenport, Auburndale' },
  'lake-county': { name: 'Lake County', cities: 'Clermont, Leesburg, Eustis, Tavares, Mount Dora, Lady Lake' },
  'marion-county': { name: 'Marion County', cities: 'Ocala, Belleview, Dunnellon, Silver Springs, The Villages' },
  'sumter-county': { name: 'Sumter County', cities: 'Bushnell, Wildwood, The Villages, Center Hill, Coleman' },
  'putnam-county': { name: 'Putnam County', cities: 'Palatka, Crescent City, Interlachen, Pomona Park, Welaka' },
  'volusia-county': { name: 'Volusia County', cities: 'Daytona Beach, Deltona, DeLand, Port Orange, Ormond Beach, New Smyrna Beach' },
  'flagler-county': { name: 'Flagler County', cities: 'Palm Coast, Flagler Beach, Bunnell, Beverly Beach' },
};

export default function LocationPage({ onOpenLeadForm, onOpenEstimateForm }) {
  const { slug } = useParams();
  const location = LOCATIONS[slug];

  if (!location) {
    return (
      <div className="min-h-screen bg-white">
        <Header onOpenLeadForm={onOpenLeadForm} onOpenEstimateForm={onOpenEstimateForm} />
        <div className="max-w-4xl mx-auto px-4 py-32 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Location Not Found</h1>
          <Link to="/" className="text-emerald-600 hover:text-emerald-700 font-medium">Back to Home</Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="location-page">
      <Header onOpenLeadForm={onOpenLeadForm} />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-600 py-16 lg:py-20">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="w-5 h-5 text-emerald-200" />
            <span className="text-emerald-200 font-medium text-sm">{location.name}, Florida</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4" data-testid="location-title">
            AC Installation in {location.name}
          </h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto mb-6">
            Looking for AC installation near me in {location.name}? Appliance Direct provides professional HVAC installation with Goodman heat pump systems, 10 Year Factory Warranty, and financing from $98/mo.
          </p>
          <div className="flex justify-center">
            <Link
              to="/installation"
              className="bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-4 rounded-lg transition-colors text-base border border-white/20"
              data-testid="location-cta-products"
            >
              View Systems & Pricing
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3 text-center">
            HVAC Installation Services in {location.name}
          </h2>
          <p className="text-gray-600 text-center max-w-2xl mx-auto mb-12">
            Professional air conditioner installation near me serving {location.cities}. New AC unit installation, AC system installation, and home AC installation by licensed and insured technicians.
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">10 Year Factory Warranty</h3>
              <p className="text-gray-600 text-sm">Every AC installation in {location.name} includes a 10 Year Factory Warranty for complete peace of mind.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-testid="location-permits-box">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">All Permits Included</h3>
              <p className="text-gray-600 text-sm">Your new AC unit installation includes all required {location.name} permits and standard installation.</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-100">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
                <Phone className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Easy Financing</h3>
              <p className="text-gray-600 text-sm">Home AC installation from $98/mo. No money down, no pre-payment penalty. 450+ credit score approved.</p>
            </div>
          </div>

          {/* What's Included */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8">
            <h3 className="font-bold text-gray-900 text-xl mb-4">What's Included with AC System Installation in {location.name}</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Goodman Heat Pump Condenser',
                'Air Handler',
                'Heating Element',
                'Thermostat',
                'Standard Installation',
                'All Required Permits',
                '10 Year Factory Warranty',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-gray-700">
                  <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span className="text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Teaser */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Affordable New AC Unit Installation in {location.name}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            Goodman 15.2 SEER2 R32 Heat Pump Systems from 1.5 to 5 Ton. HVAC installation near me starting at just $98/mo with approved financing.
          </p>
          <Link
            to="/installation"
            className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-lg transition-colors text-base"
            data-testid="location-view-pricing"
          >
            View All Systems & Pricing <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-3">
            Cities We Serve in {location.name}
          </h2>
          <p className="text-gray-600 mb-6">
            {location.cities}
          </p>
          <p className="text-sm text-gray-500">
            Appliance Direct provides air conditioner installation near me throughout {location.name}, FL. Whether you need a new AC unit installation, AC system installation, or a complete home AC installation, our licensed and insured partners deliver professional HVAC installation with Goodman heat pump systems.
          </p>
        </div>
      </section>

      <Footer />
      <AccessibilityWidget />
    </div>
  );
}
